import React, { useEffect, useRef, useState } from 'react';
import { Download, FileText, Printer, X } from 'lucide-react';
import { apiClient } from '../utils/apiClient';

const iconStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem', cursor: 'pointer' };

const LessonHistoryPdf = ({ sessionId }) => {
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [retry, setRetry] = useState(0);
  const frame = useRef(null);
  const trigger = useRef(null);
  const close = useRef(null);

  useEffect(() => {
    if (!open) return;
    close.current?.focus();
    const handleKey = event => {
      if (event.key === 'Escape') { event.stopPropagation(); setOpen(false); }
      if (event.key === 'Tab') {
        const controls = [...close.current.parentElement.querySelectorAll('button:not(:disabled), a[href]')];
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleKey, true);
    const button = trigger.current;
    return () => { document.removeEventListener('keydown', handleKey, true); button?.focus(); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    let url;
    setLoading(true);
    setError('');
    setPdfUrl('');
    setReady(false);
    const load = async () => {
      try {
        if (!sessionId) throw new Error('Sign in again to export your lessons.');
        const response = await apiClient('/current/lessons/pdf', {
          headers: { 'session-id': sessionId, Accept: 'application/pdf' },
          signal: controller.signal
        });
        if (!response.ok) {
          const isJson = response.headers.get('content-type')?.includes('application/json');
          const detail = isJson ? await response.json() : null;
          throw new Error(detail?.error || detail?.message || `Unable to load lesson history PDF (${response.status}).`);
        }
        const blob = await response.blob();
        if (!blob.size || blob.type.split(';')[0].toLowerCase() !== 'application/pdf') {
          throw new Error('The server did not return a valid lesson history PDF.');
        }
        if (controller.signal.aborted) return;
        url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (failure) {
        if (!controller.signal.aborted) setError(failure.message || 'Unable to load lesson history PDF.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => {
      controller.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, sessionId, retry]);

  const print = () => {
    try {
      if (!frame.current?.contentWindow) throw new Error('PDF preview is not ready.');
      frame.current.contentWindow.focus();
      frame.current.contentWindow.print();
    } catch {
      setError('Unable to print from this browser. Download the PDF and print it from your PDF viewer.');
    }
  };

  return <>
    <button ref={trigger} type="button" title="View lesson history PDF" aria-label="View lesson history PDF" onClick={() => setOpen(true)} style={iconStyle}>
      <FileText size={20} aria-hidden="true" />
    </button>
    {open && <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div role="dialog" aria-modal="true" aria-labelledby="lesson-history-pdf-title" style={{ backgroundColor: 'white', width: '95vw', height: '92vh', borderRadius: '8px', padding: '1rem', display: 'flex', flexDirection: 'column', color: '#222' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <h3 id="lesson-history-pdf-title" style={{ margin: 0, flex: 1 }}>Lesson history PDF</h3>
          {pdfUrl && <a href={pdfUrl} download="lesson-history.pdf" title="Download lesson history PDF" aria-label="Download lesson history PDF" style={iconStyle}><Download size={20} aria-hidden="true" /></a>}
          <button type="button" disabled={!ready} onClick={print} title="Print lesson history PDF" aria-label="Print lesson history PDF" style={iconStyle}><Printer size={20} aria-hidden="true" /></button>
          <button ref={close} type="button" onClick={() => setOpen(false)} title="Close PDF preview" aria-label="Close PDF preview" style={iconStyle}><X size={20} aria-hidden="true" /></button>
        </div>
        {loading && <p role="status">Loading lesson history PDF...</p>}
        {error && <p role="alert">{error} {!pdfUrl && <button type="button" onClick={() => setRetry(value => value + 1)}>Retry</button>}</p>}
        {pdfUrl && <iframe ref={frame} src={pdfUrl} title="Lesson history PDF" onLoad={() => setReady(true)} style={{ flex: 1, minHeight: 0, width: '100%', border: '1px solid #ddd' }} />}
        {pdfUrl && <p style={{ margin: '0.5rem 0 0' }}>If the preview is not supported by your browser, use the download icon to open the PDF.</p>}
      </div>
    </div>}
  </>;
};

export default LessonHistoryPdf;
